import React from 'react';
import { cn } from '~/lib/utils';
import styles from './AnswerChip.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface AnswerChipProps {
  /** The answer itself, e.g. `Existing investors`. Rendered on one line — the row wraps, not the chip. */
  label: string;
  /** Fill + light-25 rim + default-tone label. One chip in a group carries it; Paper preselects the first. */
  selected?: boolean;
  onSelect?: () => void;
  theme?: ChatTheme;
  className?: string;
}

/**
 * One answer to the agent's clarifying question (*Asking back*, the *Founder · cold start*
 * thread).
 *
 * 34px tall, 14/20 at weight 500, rim always present. **Not `PillButton`** — that one is 30px with a
 * 13/18 label and fires a command; this one is 34px and records a choice, and the selected rim at
 * light-25 is the only place that step is used as a border in the agent column. The two are kept
 * apart on purpose; merging them costs 4px of height and the whole distinction.
 *
 * Paper draws the chip as a `div`. It is a real `<button>` here — it is the only thing in the
 * question block the user can act on — and carries `aria-pressed` so a screen reader hears which
 * answer is already picked.
 */
export function AnswerChip({
  label,
  selected = false,
  onSelect,
  theme: themeProp,
  className,
}: AnswerChipProps) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      type="button"
      className={cn(chatThemeClass(theme), styles.chip, selected && styles.selected, className)}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export interface AnswerChipRowProps {
  /** `AnswerChip`s. Paper never draws anything else in here. */
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The wrapping row the chips live in — `flex-wrap` with a 6px gap and nothing else.
 *
 * Trivial, and still its own part: the wrap and the gap are the two numbers that decide whether
 * three answers read as one group or as three loose buttons, and they belong in one file rather
 * than restated at every call site.
 */
export function AnswerChipRow({ children, theme: themeProp, className }: AnswerChipRowProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.row, className)}>{children}</div>
  );
}
